import { ReactNode, useEffect, useRef, useState } from 'react';

import {
  Box,
  BoxProps,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Skeleton,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { Card } from '~/components/card';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency, billingFrequencySchema } from '~/features/checkout/models/checkout';
import { PriceCardUsp } from '~/features/experiments/ab_price_card_usps/components/price-card-usp';
import { usePriceCardUspsVariant } from '~/features/experiments/ab_price_card_usps/hooks';
import {
  getPriceCardFeatures,
  getUspFeatureKey,
} from '~/features/experiments/ab_price_card_usps/utils/get-price-card-features';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { useStandaloneProgramPrice } from '~/features/plans/hooks/use-standalone-program-price';
import { Program } from '~/features/program/models/program';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';

import { shouldSeeAnnualBundlePlan } from '../../../checkout/utils/should-see-annual-plan';
import { CheckIcon } from '../icons';

import { CountdownCompactContainer } from './countdown-compact';
import { CouponAppliedTag } from './coupon-applied-tag';
import { MobileStickyBanner } from './mobile-sticky-banner';
import { PricePlanRadioGroup } from './plan-radio';

const STANDALONE_FEATURES = [
  '**Flexibility to finish the program in your own time**',
  'Access to the individual program',
  'Hands-on projects with expert feedback',
  'Personalized project reviews',
  'Program Certificate',
];

const ALL_ACCESS_FEATURES = [
  '**Cancel Anytime**',
  '**Unlimited access to hundreds of top-rated courses**',
  'Hands-on projects with expert feedback',
  'Personalized career coaching and interview prep',
  'Program Certificates',
];

export type PriceCardProps = StaticPriceCardProps & {
  renderButton?: (selectedPlan: BillingFrequency, buttonLabel: string) => ReactNode;
  isMobile?: boolean;
  defaultSelectedPlan?: BillingFrequency;
} & BoxProps;

export function PriceCard({
  programKey,
  programTitle,
  programSemanticType,
  renderButton,
  isMobile = false,
  defaultSelectedPlan,
  ...cardProps
}: PriceCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { currency } = useCurrency();
  const isBundleAnnualPlan = shouldSeeAnnualBundlePlan(currency);
  const [selectedPlan, setSelectedPlan] = useState<BillingFrequency>(
    defaultSelectedPlan === billingFrequencySchema.enum['4month'] && isBundleAnnualPlan
      ? billingFrequencySchema.enum['annually']
      : defaultSelectedPlan ?? billingFrequencySchema.enum.monthly
  );
  const [showMobileBanner, setShowMobileBanner] = useState<boolean>(false);
  const { data: plans } = usePricePlans(currency);
  const { data: standalonePlan, isFetched: standalonePlanIsFetched } = useStandaloneProgramPrice({
    nodeKey: programKey,
    currency,
  });
  const { data: promoData } = usePromotion();
  const buttonLabel = promoData?.countdownConfig?.buttonLabelOverride ?? 'Enroll Now';
  const uspsVariant = usePriceCardUspsVariant();
  const subscriptionFeatures = getPriceCardFeatures({
    fallbackAllAccessFeatures: ALL_ACCESS_FEATURES,
    fallbackStandaloneFeatures: STANDALONE_FEATURES,
    planType: 'subscription',
    variant: uspsVariant,
  });
  const standaloneFeatures = getPriceCardFeatures({
    fallbackAllAccessFeatures: ALL_ACCESS_FEATURES,
    fallbackStandaloneFeatures: STANDALONE_FEATURES,
    planType: 'standalone',
    variant: uspsVariant,
  });
  const isBundlePlanSelected =
    selectedPlan === billingFrequencySchema.enum['4month'] || selectedPlan === billingFrequencySchema.enum['annually'];

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        }
      },
      {
        threshold: 0,
      }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const monthlyPlan = plans?.prices[billingFrequencySchema.enum.monthly]?.individual;
  const bundlePlan = plans?.prices[isBundleAnnualPlan ? 'annually' : '4month']?.individual;

  const ALL_ACCESS_PLAN_ITEMS = [
    {
      label: 'Subscription · Bundle',
      value: isBundleAnnualPlan ? billingFrequencySchema.enum['annually'] : billingFrequencySchema.enum['4month'],
      features: subscriptionFeatures,
      ...bundlePlan,
      displayPrice: bundlePlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
      strikeThroughDisplayPrice: bundlePlan?.strikeThroughDisplayPricePerMonth,
    },
    {
      label: 'Subscription · Monthly',
      value: billingFrequencySchema.enum.monthly,
      features: subscriptionFeatures,
      ...monthlyPlan,
      displayPrice: monthlyPlan?.displayPricePerMonth,
      displayPriceUnits: ' / month',
    },
  ];

  const STANDALONE_PLAN_ITEM = {
    label: 'Individual Course',
    value: billingFrequencySchema.enum.purchase,
    features: standaloneFeatures,
    ...standalonePlan,
    displayPriceUnits: ' / one-time payment',
  };

  const activePlan =
    selectedPlan === billingFrequencySchema.enum.purchase
      ? STANDALONE_PLAN_ITEM
      : ALL_ACCESS_PLAN_ITEMS.find((item) => item.value === selectedPlan);

  const cardTitle = activePlan?.value === billingFrequencySchema.enum.purchase ? programTitle : activePlan?.label;

  function renderPrice({
    billingFrequency,
    displayPrice,
    displayPriceUnits,
    strikeThroughDisplayPrice,
    couponApplied,
    couponPercentOff,
    savingsBlurb,
  }: {
    billingFrequency: BillingFrequency;
    displayPrice: string | undefined;
    displayPriceUnits: string | undefined;
    strikeThroughDisplayPrice: string | null | undefined;
    couponApplied: boolean | undefined;
    couponPercentOff: number | null | undefined;
    savingsBlurb: string | null | undefined;
  }) {
    if (!displayPrice)
      return (
        <Flex alignItems='center' flexDirection='row' gap={2}>
          <Skeleton height='40px' width='120px' />
          <Skeleton height='27px' width='120px' />
        </Flex>
      );
    else if (strikeThroughDisplayPrice) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='supportGray.60'
                fontFamily='body'
                position='relative'
                size='heading-4'
                textDecor='line-through'
                textDecorationThickness='2px'
              >
                {strikeThroughDisplayPrice}
              </Heading>
            </Heading>
            {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading
            alignItems='center'
            as='ins'
            color='supportGray.100'
            display='flex'
            flexWrap='wrap'
            fontFamily='body'
            fontWeight='600'
            gap={2}
            size='heading-4'
            textDecor='none'
          >
            {displayPrice}{' '}
            <Heading as='span' fontFamily='body' fontSize='md' lineHeight='md'>
              {displayPriceUnits}
            </Heading>
          </Heading>
        </>
      );
    } else {
      return (
        <Flex flexWrap='wrap' gap={3}>
          <Heading
            alignItems='center'
            as='p'
            color='black'
            display='flex'
            fontFamily='body'
            fontSize='2.75rem'
            fontWeight='500'
            gap={2}
          >
            {displayPrice}{' '}
            <Text as='span' fontSize='lg'>
              {displayPriceUnits}
            </Text>
          </Heading>
          {isBundlePlanSelected && !couponApplied && savingsBlurb && (
            <Tag alignSelf='center' as='span' colorScheme='green'>
              <TagLabel>{savingsBlurb}</TagLabel>
            </Tag>
          )}
        </Flex>
      );
    }
  }

  function getAvailablePlans() {
    if (programSemanticType === 'Course') return [];
    // Note: this is a way to prevent layout shifts when standalone plan is fetching.
    // Since Nanodegree programs _should_ have a standalone plan, we will optimistically show the standalone plan with a skeleton over the price.
    if (!standalonePlanIsFetched) return [...ALL_ACCESS_PLAN_ITEMS, STANDALONE_PLAN_ITEM];

    if (standalonePlanIsFetched && !standalonePlan) return ALL_ACCESS_PLAN_ITEMS;

    return [...ALL_ACCESS_PLAN_ITEMS, STANDALONE_PLAN_ITEM];
  }

  function handlePlanChange(plan: BillingFrequency) {
    let label = `${plan}_pricecard_clicked`;
    if (plan === billingFrequencySchema.enum.purchase) {
      label = 'onetime_purchase_pricecard_clicked';
    }
    trackEvent({
      name: 'Product Page CTA Clicked',
      type: 'click',
      label,
      additionalAttributes: {
        product_ids: [programKey],
      },
    });

    setSelectedPlan(plan);
  }

  const availablePlans = getAvailablePlans();

  return (
    <>
      <Card
        ref={containerRef}
        bgColor='supportGray.10'
        borderColor='supportBlue.20'
        borderRadius='12px'
        data-testid={isMobile ? 'mobile-plans' : 'desktop-plans'}
        display='flex'
        flexDirection='column'
        gap={4}
        padding='8px'
        scrollMarginTop={`calc(${headerHeight} + 16px)`}
        {...cardProps}
      >
        <PricePlanRadioGroup items={availablePlans} value={selectedPlan} onChange={handlePlanChange} />
        <Flex flex='1' flexDirection='column' gap={2} padding='0 12px 12px'>
          <Heading as='p' size='heading-7'>
            {cardTitle}
            {isBundlePlanSelected && (
              <Box as='sup' fontSize='sm' visibility={cardTitle ? 'visible' : 'hidden'}>
                1
              </Box>
            )}
          </Heading>

          {activePlan && (
            <>
              {renderPrice({
                displayPrice: activePlan.displayPrice,
                displayPriceUnits: activePlan.displayPriceUnits,
                strikeThroughDisplayPrice: activePlan.strikeThroughDisplayPrice,
                couponApplied: activePlan.couponApplied,
                couponPercentOff: activePlan.couponPercentOff,
                billingFrequency: activePlan.value,
                savingsBlurb:
                  activePlan.value === billingFrequencySchema.enum['4month'] ||
                  activePlan.value === billingFrequencySchema.enum['annually']
                    ? activePlan.savingsBlurb
                    : undefined,
              })}
              <List
                borderTop='1px solid'
                borderTopColor='accent.sky.100'
                display='flex'
                flexDirection='column'
                gap={uspsVariant === 'variant_b' ? 1 : 2}
                py={uspsVariant === 'variant_b' ? 2 : 4}
              >
                {activePlan.features.map((feature) => (
                  <ListItem key={getUspFeatureKey(feature)} display='flex'>
                    <ListIcon alignSelf='flex-start' as={CheckIcon} color='supportGray.50' height='20px' width='20px' />
                    <PriceCardUsp feature={feature} variant={uspsVariant} />
                  </ListItem>
                ))}
              </List>

              <CountdownCompactContainer
                renderButton={(plan, buttonLabel) => renderButton?.(plan, buttonLabel)}
                selectedPlan={selectedPlan}
              />
            </>
          )}

          {isBundlePlanSelected && (
            <List as='ol' color='gray.700' display='flex' flexDirection='column' gap={1}>
              <ListItem>
                <Text fontSize='2xs'>
                  <Box as='sup'>1</Box>Discount applies to the first {isBundleAnnualPlan ? 'year' : '4 months'} of
                  membership, after which plans are converted to month-to-month.
                </Text>
              </ListItem>
              {selectedPlan === billingFrequencySchema.enum['4month'] && (
                <ListItem>
                  <Text fontSize='2xs'>
                    <Box as='sup'>2</Box>Total price of {bundlePlan?.displayPrice} for a 4-month bundle, based on the
                    average time to complete a Nanodegree program.
                  </Text>
                </ListItem>
              )}
              {selectedPlan === billingFrequencySchema.enum['annually'] && (
                <ListItem>
                  <Text fontSize='2xs'>
                    <Box as='sup'>2</Box>Total price of {bundlePlan?.displayPrice} for an annual subscription.
                  </Text>
                </ListItem>
              )}
            </List>
          )}
        </Flex>
      </Card>
      {isMobile && (
        <MobileStickyBanner buttonSlot={renderButton?.(selectedPlan, buttonLabel)} isVisible={showMobileBanner} />
      )}
    </>
  );
}

export function transformStaticPriceCardProps({ program }: { program: Program }) {
  return {
    programKey: program.key,
    programSemanticType: program.semanticType,
    programTitle: program.title,
  };
}

export type StaticPriceCardProps = ReturnType<typeof transformStaticPriceCardProps>;
